import { getDatabase, ref, push, set } from "firebase/database";
import { initializeApp } from "firebase/app";
import { getDeviceName } from "../helpers";

// Config variables
const API_KEY = process?.env?.REACT_APP_FIREBASE_API_KEY;
const AUTH_DOMAIN = process?.env?.REACT_APP_FIREBASE_AUTH_DOMAIN;
const DATABASE_URL =
  process?.env?.REACT_APP_TYPE === "production"
    ? process?.env?.REACT_APP_FIREBASE_DATABASE_URL
    :
     process?.env?.REACT_APP_UAT_FIREBASE_DATABASE_URL;
const PROJECT_ID = process?.env?.REACT_APP_FIREBASE_PROJECT_ID;
const STORAGE_BUCKET = process?.env?.REACT_APP_FIREBASE_STORAGE_BUCKET;
const MESSAGING_SENDER_ID =
  process?.env?.REACT_APP_FIREBASE_MESSAGING_SENDER_ID;
const APP_ID = process?.env?.REACT_APP_FIREBASE_APP_ID;
const MEASUREMENT_ID = process?.env?.REACT_APP_FIREBASE_MEASUREMENT_ID;

const GOOPS_INGEST_URL =
  process?.env?.REACT_APP_GOOPS_EVENTS_INGEST_URL ||
  "https://laravelapi.gogmgo.com/goops/ingest-event";
const GOOPS_INGEST_TOKEN = process?.env?.REACT_APP_GOOPS_EVENTS_INGEST_TOKEN || "";

const firebaseConfig = {
  apiKey: API_KEY,
  authDomain: AUTH_DOMAIN,
  databaseURL: DATABASE_URL,
  projectId: PROJECT_ID,
  storageBucket: STORAGE_BUCKET,
  messagingSenderId: MESSAGING_SENDER_ID,
  appId: APP_ID,
  measurementId: MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const firebaseDB = getDatabase(app);

const opsEventCodeForName = (eventName) => {
  const normalized = String(eventName || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (normalized === "order_created") {
    return "POS_order_created";
  }
  if (normalized === "order_updated") {
    return "POS_order_updated";
  }
  if (normalized === "pay_bill") {
    return "POS_pay_bill";
  }
  if (normalized.indexOf("order_") === 0) {
    return `POS_${normalized}`;
  }

  return "POS_general_event";
};

const firstNonEmpty = (...values) => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value === null || value === undefined) {
      continue;
    }
    const trimmed = String(value).trim();
    if (trimmed !== "" && trimmed !== "null" && trimmed !== "undefined") {
      return trimmed;
    }
  }
  return null;
};

/**
 * Build flat loyalty/CRM fields from QR user_detail for ops meta_data.
 */
const buildUserLoyaltyMeta = (user_detail) => {
  if (!user_detail || typeof user_detail !== "object") {
    return {
      loyalty_logged_in: false,
    };
  }

  const crmId = firstNonEmpty(
    user_detail.id,
    user_detail.customer_id,
    user_detail.qr_customer_id
  );
  const loyaltyId = firstNonEmpty(
    user_detail.loyalty_member_id,
    user_detail.loyalty_id,
    user_detail.eber_member_id,
    user_detail.external_member_id,
    user_detail.CardNo,
    user_detail.card_no,
    user_detail.member_id
  );
  const loyaltyType = firstNonEmpty(
    user_detail.loyalty_type,
    user_detail.loyaltyType
  );
  const externalMemberId = firstNonEmpty(
    user_detail.external_member_id,
    user_detail.eber_member_id
  );
  const email = firstNonEmpty(
    user_detail.contact_email,
    user_detail.email
  );
  const phone = firstNonEmpty(
    user_detail.contact_no,
    user_detail.phone,
    user_detail.mobile
  );
  const firstName = firstNonEmpty(user_detail.first_name);
  const lastName = firstNonEmpty(user_detail.last_name);
  const loyaltyLoggedIn = Boolean(loyaltyId || externalMemberId);

  const meta = {
    loyalty_logged_in: loyaltyLoggedIn,
  };

  if (crmId) {
    meta.crm_id = crmId;
    meta.qr_customer_id = crmId;
  }
  if (loyaltyId) {
    meta.loyalty_id = loyaltyId;
  }
  if (loyaltyType) {
    meta.loyalty_type = loyaltyType;
  }
  if (externalMemberId) {
    meta.external_member_id = externalMemberId;
  }
  if (email) {
    meta.customer_email = email;
  }
  if (phone) {
    meta.customer_phone = phone;
  }
  if (firstName) {
    meta.customer_first_name = firstName;
  }
  if (lastName) {
    meta.customer_last_name = lastName;
  }

  return meta;
};

/**
 * Compact item + promo summary for Order Created ops meta_data.
 */
const buildOrderSummaryMeta = (order_context) => {
  if (!order_context || typeof order_context !== "object") {
    return {};
  }

  const rawItems = Array.isArray(order_context.menu_items)
    ? order_context.menu_items
    : [];
  const items = [];

  for (let i = 0; i < rawItems.length; i++) {
    const item = rawItems[i];
    if (!item || item.void === true || item.void === "true") {
      continue;
    }
    const name = firstNonEmpty(
      item.pos_name,
      item.menu_item_name,
      item.name,
      item.item_name,
      item.bundleName
    );
    const qtyRaw =
      item.menu_item_quantity ?? item.quantity ?? item.qty ?? 0;
    const quantity = Number(qtyRaw);
    const safeQty = Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    if (!name && safeQty <= 0) {
      continue;
    }
    items.push({
      name: name || "Item",
      quantity: safeQty,
    });
  }

  const rawPromos = Array.isArray(order_context.promotions)
    ? order_context.promotions
    : [];
  const promotions = [];
  const seenPromoKeys = {};

  const pushPromo = (promo) => {
    if (!promo || typeof promo !== "object") {
      return;
    }
    const name = firstNonEmpty(
      promo.name,
      promo.voucherName,
      promo.promotion_name,
      promo.Code,
      promo.Name
    );
    const code = firstNonEmpty(promo.code, promo.VoucherNo, promo.Code);
    const type = firstNonEmpty(promo.type, promo.Type);
    const discountRaw =
      promo.promotionTotaldiscount ??
      promo.discountAmt ??
      promo.amount ??
      promo.TypeValue ??
      0;
    const discount = Number(discountRaw);
    if (!name && !code) {
      return;
    }
    const key = `${name || ""}|${code || ""}|${type || ""}`;
    if (seenPromoKeys[key]) {
      return;
    }
    seenPromoKeys[key] = true;
    const row = {};
    if (name) {
      row.name = name;
    }
    if (code) {
      row.code = code;
    }
    if (type) {
      row.type = type;
    }
    if (Number.isFinite(discount) && discount !== 0) {
      row.discount = discount;
    }
    promotions.push(row);
  };

  for (let i = 0; i < rawPromos.length; i++) {
    pushPromo(rawPromos[i]);
  }

  // Also pick up item-level promotions (e.g. Re-Price / item deals).
  for (let i = 0; i < rawItems.length; i++) {
    const item = rawItems[i];
    if (!item) {
      continue;
    }
    if (Array.isArray(item.promotions)) {
      for (let j = 0; j < item.promotions.length; j++) {
        pushPromo(item.promotions[j]);
      }
    } else if (item.promotions && typeof item.promotions === "object") {
      pushPromo(item.promotions);
    }
  }

  const meta = {};

  if (items.length > 0) {
    // Summary string only — nested `items` flattens into noisy detail rows in ERP.
    meta.items_summary = items
      .map((row) => `${row.name}(${row.quantity})`)
      .join(", ");
  }

  if (promotions.length > 0) {
    meta.promos_applied = promotions
      .map((row) => {
        const label = row.name || row.code || "Promo";
        const codePart =
          row.code && row.code !== label ? ` (${row.code})` : "";
        const discountPart =
          row.discount != null ? ` [-${row.discount}]` : "";
        return `${label}${codePart}${discountPart}`;
      })
      .join("; ");
  } else if (items.length > 0) {
    meta.promos_applied = "none";
  }

  return meta;
};

/**
 * Build display title like:
 * Order Created - Item A(1), Item B(3)
 * Order Updated - Item A(1), Item B(3)
 */
const buildEventDisplayName = (eventName, order_context) => {
  const baseName = String(eventName || "").trim();
  if (!baseName) {
    return baseName;
  }
  const summaryMeta = buildOrderSummaryMeta(order_context);
  const itemsSummary = summaryMeta.items_summary;
  if (!itemsSummary) {
    return baseName;
  }
  if (baseName.indexOf(itemsSummary) !== -1) {
    return baseName;
  }
  return `${baseName} - ${itemsSummary}`;
};

/**
 * Best-effort GoOps ingest. Never awaited by callers — must not block order flow.
 */
const sendOpsOrderHistoryEvent = ({
  merchantId,
  orderId,
  offlineId,
  eventName,
  userName,
  deviceName,
  appVersion,
  timeStamp,
  user_detail,
  order_context,
}) => {
  try {
    const merchant = Number(merchantId);
    if (!merchant || !eventName || !GOOPS_INGEST_URL || !GOOPS_INGEST_TOKEN) {
      return;
    }

    const reportedOn = new Date(
      (Number(timeStamp) > 0 ? Number(timeStamp) : Date.now() / 1000) * 1000
    ).toISOString();

    const loyaltyMeta = buildUserLoyaltyMeta(user_detail);
    const orderSummaryMeta = buildOrderSummaryMeta(order_context);
    let displayEventName = buildEventDisplayName(eventName, order_context);
    // GoOps event_title column / validation is max 255 chars.
    if (displayEventName.length > 255) {
      displayEventName = displayEventName.slice(0, 252) + "...";
    }

    const payload = {
      merchant_id: merchant,
      event_code: opsEventCodeForName(eventName),
      event_title: displayEventName,
      module: "POS",
      sub_module: "Order",
      severity: "info",
      source: "QR",
      platform: "Web",
      app_version: appVersion || undefined,
      device_id: deviceName || undefined,
      user_name: userName || undefined,
      order_id: orderId != null ? String(orderId) : undefined,
      offline_order_id: offlineId != null ? String(offlineId) : undefined,
      short_description: displayEventName,
      event_description: displayEventName,
      reported_on: reportedOn,
      meta_data: {
        legacy_event_name: eventName,
        event_name: String(eventName || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_"),
        order_id: orderId != null ? String(orderId) : undefined,
        offline_order_id: offlineId != null ? String(offlineId) : undefined,
        merchant_id: String(merchant),
        device_name: deviceName || undefined,
        user_name: userName || undefined,
        channel: "ordrr_online",
        ...loyaltyMeta,
        ...orderSummaryMeta,
      },
    };

    fetch(GOOPS_INGEST_URL, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Goops-Event-Source": "QR",
        "X-Goops-Event-Token": GOOPS_INGEST_TOKEN,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Ignore network errors — telemetry must never affect checkout.
    });
  } catch (error) {
    // Ignore — telemetry must never affect checkout.
  }
};

export const handleSendDataFireBase = async ({
  offline_id,
  orderId,
  user_detail,
  eventName,
  merchantId,
  order_context,
}) => {
  const { device, os, browser } = getDeviceName();
  const eventRef = push(ref(firebaseDB));
  const eventId = eventRef.key;
  const customerName = user_detail?.first_name + " " + user_detail?.last_name;
  const displayEventName = buildEventDisplayName(eventName, order_context);

  // Order data
  const orderData = {
    timeStamp: Date.now() / 1000,
    eventName: displayEventName,
    userName: customerName,
  };

  if (device?.type) {
    orderData.deviceName = device?.model;
  } else {
    orderData.deviceName = os?.name;
  }
  
  if (browser?.name) {
    orderData.appVersion = browser?.name;
  }

  const orderRefPath = `OrderHistory/${merchantId}/${orderId}/${offline_id}/events/${eventId}`;

  // Mirror to GoOps without blocking Firebase / order placement.
  sendOpsOrderHistoryEvent({
    merchantId,
    orderId,
    offlineId: offline_id,
    eventName,
    userName: customerName,
    deviceName: orderData.deviceName,
    appVersion: orderData.appVersion,
    timeStamp: orderData.timeStamp,
    user_detail,
    order_context,
  });

  try {
    await set(ref(firebaseDB, orderRefPath), orderData);
    
  } catch (error) {
    console.error("Error sending order data:", error);
  }
};

export { firebaseDB, ref, push, set };
